/**
 * rptTemplateFormConfig.js — CẤU HÌNH FORM THÊM/SỬA MẪU IN & XUẤT (model `rpt`).
 *
 * Dùng cho PrintActionDrawer: người dùng tạo/sửa mẫu ngay tại chỗ đang xem báo cáo/danh sách,
 * không phải mở màn hình quản lý riêng.
 *
 * Danh sách field lấy từ CẤU HÌNH LISTINFO CŨ của `rpt` (nhãn, bắt buộc, thứ tự — công ty có thể
 * đã sửa) rồi được LÀM GIÀU tại đây: đổi ô nhập thô thành trình soạn mã, gắn hướng dẫn cụ thể cho
 * từng loại mẫu, ẩn/hiện theo loại mẫu đang chọn. Khi không đọc được listinfo thì dùng
 * LISTINFO_RPT_MAC_DINH bên dưới để form vẫn chạy.
 *
 * Ba loại mẫu:
 *   - html         : in trực tiếp bằng `html_template`.
 *   - file         : xuất theo file mẫu .xlsx/.docx đã upload (`file_mau_in`).
 *   - excel_config : CHỈ dành cho BÁO CÁO — `handle_data_expression` trả về JSON cấu hình excel,
 *                    hệ thống tự dựng file, không cần file mẫu.
 */

// ── Bản dự phòng khi không đọc được listinfo `rpt` ───────────────────────────────────────────
export const LISTINFO_RPT_MAC_DINH = [
	{ name: 'ma_cn', header: 'Mã chứng năng', type: 'String', required: true },
	{ name: 'ten_mau_in', header: 'Tên mẫu in', type: 'String', required: true },
	{ name: 'html_template', header: 'Source mẫu in (html)', type: 'String' },
	{ name: 'file_mau_in', header: 'File mẫu (xlxs,docx,ng,pub,ejs)', type: 'OtherFile' },
	{ name: 'handle_data_expression', header: 'Xử lý dự liệu trước khi in hoặc xuất excel', type: 'String' },
	{ name: 'cac_lien', header: 'Liên', type: 'Array' },
	{ name: 'parameters', header: 'Tham số', type: 'Array' },
];

// listinfo type -> type của DataListForm
const KIEU_LISTINFO_SANG_FORM = {
	String: 'String', Number: 'Number', Boolean: 'Boolean',
	Date: 'Date', DateTime: 'DateTime', Time: 'Time',
	Array: 'ObjectArray', Object: 'Object', Mixed: 'Mixed',
	OtherFile: 'File', File: 'File', Image: 'File',
};

export const RPT_FILE_ACCEPT = '.xlsx,.xls,.docx,.ejs,.pug,.ng,.txt';

// ── HƯỚNG DẪN HIỆN NGAY TRONG FORM ───────────────────────────────────────────────────────────
// CodeEditor hiển thị DÒNG ĐẦU khi thu gọn và toàn bộ (markdown) khi bung ra -> dòng đầu phải tóm
// được ý chính, phần sau mới đi vào cú pháp.
export const HD_HTML = [
	'**Mẫu in HTML** — bấm để xem cú pháp: `${...}` cho giá trị, `{{tb:mảng.field}}` cho dòng bảng.',
	'',
	'| Mục đích | Cú pháp |',
	'|---|---|',
	'| Giá trị | `${data.so_ct}`, `${data.company.name}` |',
	'| Ngày | `${moment(data.ngay_ct).format("DD/MM/YYYY")}` |',
	'| Số tiền | `${numeral(data.t_tien).format("#,##0")}` |',
	'| Dòng lặp trong `<td>` | `{{tb:details.ten_vt}}` — hệ thống tự thêm ng-repeat cho dòng `<tr>` |',
	'| Tên liên | `${data.lien.ten_lien}` (khi có khai Liên) |',
	'',
	'Mỗi **liên** in ra một trang. HTML được làm sạch chống XSS trước khi in.',
	'Chưa biết tên biến? Bấm **Tải JSON** ở màn hình trước để xem đúng tên field của dữ liệu.',
].join('\n');

export const HD_FILE = [
	'**File mẫu** — .xlsx dùng `<%= %>` + `[[FOR:mảng]]`; .docx dùng `{{ }}` + `{{FOR x IN mảng}}`.',
	'',
	'**Excel (.xlsx)** — giữ nguyên 100% định dạng file mẫu:',
	'',
	'- Giá trị: `<%= so_ct %>`, `<%= company.name %>`',
	'- Dòng lặp: đặt `[[FOR:details]]` ở **cột A** của dòng cần lặp, trong dòng đó dùng `<%= details.ten_vt %>`',
	'- Số thứ tự: `<%= details_index + 1 %>`',
	'- Mẫu xuất ra có thêm sheet `HD_DU_LIEU_DEBUG` liệt kê mọi biến — rất tiện để dò tên biến.',
	'',
	'**Word (.docx)**: `{{so_ct}}`, vòng lặp `{{FOR d IN details}} … {{d.ten_vt}} … {{END-FOR d}}`, điều kiện `{{IF t_tien > 0}} … {{END-IF}}`.',
].join('\n');

// Hướng dẫn viết script excel LẤY TỪ MÁY CHỦ: `${server_url}/docs/excel-config-user-guide.md`.
// Nhờ vậy sửa tài liệu ở backend là app đổi theo, không phải build lại frontend. Chuỗi dưới đây chỉ
// là bản DỰ PHÒNG khi không tải được tài liệu (mất mạng, chặn CORS, bản cũ chưa có file).
export const TEN_FILE_HUONG_DAN_EXCEL = 'excel-config-user-guide.md';

const DONG_TOM_TAT_EXCEL = '**Hướng dẫn viết script tạo file Excel** — bấm để mở toàn bộ (lấy từ tài liệu trên máy chủ).';

/**
 * Cắt lấy phần ĐẶC TẢ trong tài liệu người dùng (khối giữa hai mốc COPY) rồi gắn một dòng tóm tắt
 * lên đầu — CodeEditor chỉ hiện DÒNG ĐẦU khi thu gọn nên dòng đó phải nói được ý chính.
 */
export function chuanHoaHuongDanExcel(md) {
	const text = String(md || '');
	if (!text.trim()) return '';
	const dong = text.split('\n');
	// So khớp CẢ DÒNG: phần hướng dẫn ở đầu tài liệu có nhắc tên hai cái mốc này trong câu văn,
	// dùng indexOf sẽ bắt trúng câu văn đó và cắt sai (trước đây cắt hụt cả tài liệu).
	let batDau = dong.findIndex((l) => l.trim() === '=== COPY TỪ ĐÂY ===');
	let ketThuc = dong.findIndex((l) => l.trim() === '=== HẾT PHẦN COPY ===');
	// hai mốc nằm trong khối ``` của tài liệu -> bỏ luôn dấu rào, đừng bỏ nhầm rào của ví dụ mã
	if (batDau >= 0) { batDau += 1; if ((dong[batDau] || '').trim() === '```') batDau += 1; }
	if (ketThuc > 0 && (dong[ketThuc - 1] || '').trim() === '```') ketThuc -= 1;
	const than = (batDau >= 0 && ketThuc > batDau ? dong.slice(batDau, ketThuc) : dong).join('\n').trim();
	if (!than) return '';
	return [DONG_TOM_TAT_EXCEL, '', than].join('\n');
}

export const HD_SCRIPT_EXCEL = [
	'**Script sinh file Excel** — trả về `return { sheets: [...] }`; KHÔNG cần file mẫu. Bấm để xem khung sườn.',
	'',
	'Biến có sẵn: `data.detail` (mảng dòng báo cáo), `data.company`, các điều kiện lọc (`data.tu_ngay_string`…), `moment`, `numeral`, `utils`.',
	'',
	'Thứ tự dòng trong sheet: `headerBlocks` (mỗi phần tử = 1 dòng) → dòng tiêu đề cột → dữ liệu → `summaryRows`.',
	'',
	'```js',
	'var rows = Array.isArray(data.detail) ? data.detail : [];',
	'return { sheets: [{ name: "Báo cáo", autoFitColumns: true,',
	'  headerBlocks: [{ value: "BÁO CÁO", style: { font: { bold: true, size: 14 } } }],',
	'  columns: [{ header: "STT", key: "stt", width: 6 },',
	'            { header: "Tên", key: "ten_vt", width: 30 },',
	'            { header: "Tiền", key: "tien", type: "Number", style: { numFmt: "#,##0" } }],',
	'  summaryRows: [{ style: { font: { bold: true } },',
	'                  cells: { ten_vt: "TỔNG CỘNG", tien: { formula: "SUM(C{startRow}:C{endRow})" } } }] }] };',
	'```',
	'',
	'**Bắt buộc**: không dùng từ khoá `async`; tiền/số lượng phải là **số thật** (`Number(x) || 0`) thì Excel mới cộng được.',
	'Muốn nhờ AI viết hộ: chọn "Chèn khung mẫu" ở trên để có sẵn khung, hoặc mở hướng dẫn đầy đủ ở đầu form.',
].join('\n');

export const HD_XU_LY_DU_LIEU = [
	'**Xử lý dữ liệu trước khi in** (không bắt buộc) — nhào nặn dữ liệu rồi `return data;`.',
	'',
	'Biến: `data` (dữ liệu sắp in), `moment`, `numeral`, `utils`. Không dùng từ khoá `async`.',
	'',
	'```js',
	'data.details = (data.details || []).sort(function (a, b) { return (a.ten_vt || "").localeCompare(b.ten_vt || ""); });',
	'return data;',
	'```',
].join('\n');

export const HD_THAM_SO = [
	'Tạo biến dùng được trong mẫu. `type`: để trống = hằng số · `W` = số thành chữ · `EN` = số thành chữ tiếng Anh · `Q` = truy vấn · `E` = biểu thức JS.',
	'Ví dụ đọc số tiền thành chữ: name = `tien_bang_chu`, type = `W`, value = `t_tt`.',
].join('\n');

// ── KHUNG MẪU CHÈN SẴN ───────────────────────────────────────────────────────────────────────
const chuCaiCot = (i) => { let s = '', n = i; while (n > 0) { const m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };

const khoaCot = (c) => (typeof c.field === 'object' ? (c.field && c.field._id) || '' : c.field) || '';

/**
 * Sinh SCRIPT cấu hình excel từ chính các cột đang hiển thị của báo cáo -> người dùng có ngay một
 * mẫu chạy được, rồi mới nhờ AI hoặc tự sửa cho đẹp.
 */
export function taoScriptExcelTuCot(columns = [], tieuDe = 'BÁO CÁO') {
	const cols = (columns || []).filter((c) => khoaCot(c)).slice(0, 40);
	const dong = ['{ header: "STT", key: "stt", width: 6 }'];
	const congThuc = [];
	cols.forEach((c, i) => {
		const key = khoaCot(c);
		const nhan = String(c.label || key).replace(/"/g, "'");
		const laSo = c.type === 'Number';
		const laNgay = c.type === 'Date' || c.type === 'DateTime';
		let s = '{ header: "' + nhan + '", key: "' + key + '"';
		if (laSo) s += ', type: "Number", width: 15, style: { numFmt: "#,##0", alignment: { horizontal: "right" } } }';
		else if (laNgay) s += ', type: "' + (c.type === 'DateTime' ? 'DateTime' : 'Date') + '", width: 14, style: { numFmt: "dd/mm/yyyy", alignment: { horizontal: "center" } } }';
		else s += ', width: 22 }';
		dong.push(s);
		if (laSo) congThuc.push('    ' + key + ': { formula: "SUM(' + chuCaiCot(i + 2) + '{startRow}:' + chuCaiCot(i + 2) + '{endRow})" }');
	});
	const cotDauChuoi = cols.find((c) => c.type !== 'Number' && c.type !== 'Date' && c.type !== 'DateTime');
	const oNhanTong = cotDauChuoi ? '    ' + khoaCot(cotDauChuoi) + ': "TỔNG CỘNG",' : '    stt: "TỔNG CỘNG",';

	return [
		'// Script sinh file Excel — sinh sẵn theo các cột đang hiển thị. Sửa thoải mái.',
		'var rows = Array.isArray(data.detail) ? data.detail : [];',
		'var colLetter = function (i) { var s = "", n = i; while (n > 0) { var m = (n - 1) % 26; s = String.fromCharCode(65 + m) + s; n = Math.floor((n - 1) / 26); } return s; };',
		'',
		'// ==== CỘT: sửa nhãn, độ rộng, định dạng ở đây ====',
		'var columns = [',
		'  ' + dong.join(',\n  '),
		'];',
		'',
		'// ==== TIÊU ĐỀ PHÍA TRÊN BẢNG: mỗi phần tử là MỘT dòng ====',
		'var headerBlocks = [',
		'  { value: (data.company || {}).name || "", style: { font: { bold: true } } },',
		'  { value: "' + String(tieuDe || 'BÁO CÁO').toUpperCase().replace(/"/g, "'") + '", height: 26,',
		'    mergeCells: "A2:" + colLetter(columns.length) + "2",',
		'    style: { font: { bold: true, size: 15 }, alignment: { horizontal: "center" } } },',
		'  { value: "Từ ngày " + (data.tu_ngay_string || "...") + " đến ngày " + (data.den_ngay_string || "..."),',
		'    style: { font: { italic: true, color: { argb: "FF555555" } } } },',
		'  { value: "" }',
		'];',
		'',
		'// ==== KHÔNG CÓ DỮ LIỆU: vẫn ra file, không báo lỗi ====',
		'if (rows.length === 0) {',
		'  return { sheets: [{ name: "Dữ liệu", headerBlocks: headerBlocks.concat([{ value: "Không có dữ liệu trong kỳ đã chọn." }]) }] };',
		'}',
		'',
		'return {',
		'  ttlSeconds: 300,',
		'  sheets: [{',
		'    name: "Dữ liệu",',
		'    autoFitColumns: true,',
		'    views: [{ state: "frozen", ySplit: headerBlocks.length + 1 }],',
		'    pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true },',
		'    defaultBorder: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } },',
		'    headerBlocks: headerBlocks,',
		'    columns: columns' + (congThuc.length ? ',' : ''),
		congThuc.length ? '    summaryRows: [{ style: { font: { bold: true }, fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } } }, cells: {' : null,
		congThuc.length ? oNhanTong : null,
		congThuc.length ? congThuc.join(',\n') : null,
		congThuc.length ? '    } }]' : null,
		'  }]',
		'};',
	].filter((x) => x !== null).join('\n');
}

/** Sinh mẫu in HTML cơ bản từ các cột đang hiển thị. */
export function taoHtmlTuCot(columns = [], tieuDe = 'BÁO CÁO', mangChiTiet = 'details') {
	const cols = (columns || []).filter((c) => khoaCot(c)).slice(0, 12);
	const th = cols.map((c) => '      <th style="border:1px solid #999;padding:6px">' + String(c.label || khoaCot(c)) + '</th>').join('\n');
	const td = cols.map((c) => '      <td style="border:1px solid #999;padding:6px">{{tb:' + mangChiTiet + '.' + khoaCot(c) + '}}</td>').join('\n');
	return [
		'<div style="font-family:Arial,sans-serif;font-size:13px">',
		'  <div style="font-weight:bold">${data.company.name || ""}</div>',
		'  <div>${data.company.address || ""}</div>',
		'  <h3 style="text-align:center;margin:12px 0">' + String(tieuDe || 'BÁO CÁO').toUpperCase() + '</h3>',
		'  <div style="text-align:center;font-style:italic">${data.lien ? data.lien.ten_lien : ""}</div>',
		'  <table style="width:100%;border-collapse:collapse;margin-top:8px">',
		'    <thead><tr style="background:#f0f0f0">',
		th,
		'    </tr></thead>',
		'    <tbody><tr>',
		td,
		'    </tr></tbody>',
		'  </table>',
		'  <div style="text-align:right;margin-top:24px">Ngày ${moment().format("DD/MM/YYYY")}</div>',
		'</div>',
	].join('\n');
}

// ── File mẫu: DB lưu ĐƯỜNG DẪN tải, form lại làm việc với id file ─────────────────────────────
export const layIdFile = (giaTri) => {
	if (!giaTri) return '';
	if (typeof giaTri === 'object') return giaTri._id || giaTri.id || '';
	const s = String(giaTri).trim();
	// '/api/<id_app>/file/download/<id>' (có thể kèm ?access_token=...) -> <id>.
	// Đường dẫn khác (vd '/templates/excels/a.xlsx' của mẫu cũ) GIỮ NGUYÊN: form hiển thị/tải theo đường dẫn,
	// lưu lại cũng không đổi (taoDuongDanFile thấy có '/' thì giữ nguyên).
	const m = s.match(/\/file\/download\/([^/?#]+)/);
	return m ? m[1] : s;
};

export const taoDuongDanFile = (idFile, idApp) => {
	if (!idFile) return '';
	const s = typeof idFile === 'object' ? idFile._id || idFile.id || '' : String(idFile);
	if (!s) return '';
	if (s.indexOf('/') >= 0) return s;                       // đã là đường dẫn -> giữ nguyên
	return '/api/' + idApp + '/file/download/' + s;
};

/**
 * Script trong `handle_data_expression` là SCRIPT TẠO FILE EXCEL hay chỉ là bước tiền xử lý dữ liệu?
 * Chỉ đoán được bằng nội dung: script tạo file luôn trả về cấu hình có `sheets` (hoặc cờ excel_config).
 */
export const laScriptExcel = (script) => /(\bsheets\b|excel_config)/.test(String(script || ''));

/** Đoán loại mẫu của một bản ghi rpt đang có. */
export const doanLoaiMau = (tpl) => {
	if (!tpl) return 'html';
	if (tpl.html_template) return 'html';
	if (tpl.file_mau_in) return 'file';
	if (laScriptExcel(tpl.handle_data_expression)) return 'excel_config';
	return 'html';
};

// ── DỰNG FIELD CHO FORM ───────────────────────────────────────────────────────────────────────
const la = (loai) => (ctx, formData) => (formData || {})._loai_mau !== loai;      // ẩn khi KHÁC loại
const chiNangCao = (ctx, formData) => !(formData || {})._nang_cao;                 // ẩn khi chưa bật nâng cao

const HD_LOAI_MAU = {
	html: 'In trực tiếp ra máy in từ mẫu HTML bạn tự soạn.',
	file: 'Xuất theo file .xlsx/.docx bạn thiết kế sẵn rồi tải lên — giữ nguyên 100% định dạng của file.',
	excel_config: 'Chỉ cần một đoạn script, hệ thống tự dựng file Excel. Không phải thiết kế file mẫu.',
};

/**
 * Dựng danh sách field cho DataListForm từ CẤU HÌNH LISTINFO của `rpt`.
 *
 * @param {Array}  listinfoFields - mảng `fields` của listinfo `rpt` (name/header/type/required...)
 * @param {String} code           - mã báo cáo/danh mục đang mở (điền sẵn vào ma_cn)
 * @param {Bool}   isReport       - đang ở màn BÁO CÁO? (loại "excel bằng script" chỉ chạy cho báo cáo)
 * @param {Array}  columns        - các cột đang hiển thị, dùng để sinh khung mẫu
 * @param {String} tieuDe         - tên báo cáo, dùng cho khung mẫu
 * @param {String} huongDanExcel  - nội dung hướng dẫn tải từ máy chủ (rỗng -> dùng bản dự phòng)
 */
export function buildRptFormFields({ listinfoFields, code, isReport = true, columns = [], tieuDe = '', huongDanExcel = '' } = {}) {
	const nguon = Array.isArray(listinfoFields) && listinfoFields.length ? listinfoFields : LISTINFO_RPT_MAC_DINH;
	const theoTen = {};
	nguon.forEach((f) => { if (f && f.name) theoTen[f.name] = f; });
	const nhan = (ten, macDinh) => (theoTen[ten] && theoTen[ten].header) || macDinh;
	const batBuoc = (ten) => !!(theoTen[ten] && theoTen[ten].required);

	const loaiMauOptions = [
		{ label: 'In HTML', value: 'html' },
		{ label: 'File mẫu Excel/Word', value: 'file' },
	];
	if (isReport) loaiMauOptions.push({ label: 'Excel bằng script (không cần file)', value: 'excel_config' });

	const fields = [
		{
			field: '_loai_mau', label: 'Loại mẫu', type: 'Radio', required: true, gridEdit: { xs: 12 },
			options: loaiMauOptions,
			helpText: (ctx, formData) => HD_LOAI_MAU[(formData || {})._loai_mau] || 'Chọn cách tạo ra bản in/file.',
			// Chọn loại nào thì mồi sẵn nội dung cho loại đó (chỉ khi đang trống, không đè nội dung đã có)
			onValueChanged: (giaTriMoi, giaTriCu, formData) => {
				if (giaTriMoi === 'excel_config' && !formData.handle_data_expression) {
					return { handle_data_expression: taoScriptExcelTuCot(columns, tieuDe || code) };
				}
				if (giaTriMoi === 'html' && !formData.html_template) {
					return { html_template: taoHtmlTuCot(columns, tieuDe || code) };
				}
				return null;
			},
		},
		{
			field: '_mau_goi_y', label: 'Chèn khung mẫu', type: 'Radio', gridEdit: { xs: 12 },
			options: [
				{ label: 'Giữ nguyên', value: '' },
				{ label: 'Chèn lại khung theo cột đang hiển thị (ghi đè)', value: 'cot' },
			],
			helpText: 'Sinh sẵn một mẫu chạy được từ các cột bạn đang xem — rồi sửa tiếp hoặc nhờ AI sửa.',
			hideInForm: (ctx, formData) => ['html', 'excel_config'].indexOf((formData || {})._loai_mau) < 0,
			onValueChanged: (giaTriMoi, giaTriCu, formData) => {
				if (giaTriMoi !== 'cot') return null;
				if (formData._loai_mau === 'excel_config') {
					return { _mau_goi_y: '', handle_data_expression: taoScriptExcelTuCot(columns, tieuDe || code) };
				}
				return { _mau_goi_y: '', html_template: taoHtmlTuCot(columns, tieuDe || code) };
			},
		},
		{
			field: 'ma_cn', label: nhan('ma_cn', 'Mã chứng năng'), type: 'String', required: true,
			gridEdit: { xs: 12, sm: 6 }, readOnly: !!code,
			helpText: 'Mã của báo cáo/danh mục áp dụng mẫu này (điền sẵn theo màn hình bạn đang mở).',
		},
		{
			field: 'ten_mau_in', label: nhan('ten_mau_in', 'Tên mẫu in'), type: 'String', required: true,
			gridEdit: { xs: 12, sm: 6 },
			helpText: 'Tên người dùng thấy khi chọn mẫu lúc In/Xuất.',
		},
		{
			field: 'html_template', label: nhan('html_template', 'Source mẫu in (html)'), type: 'Code',
			gridEdit: { xs: 12 }, helpText: HD_HTML, hideInForm: la('html'), required: false,
		},
		{
			field: 'file_mau_in', label: nhan('file_mau_in', 'File mẫu (xlxs, docx, ng, pub, ejs)'), type: 'File',
			gridEdit: { xs: 12 }, accept: RPT_FILE_ACCEPT, helpText: HD_FILE, hideInForm: la('file'),
		},
		{
			// Loại "excel bằng script": chính script này TẠO RA file -> nhãn và hướng dẫn khác hẳn
			field: 'handle_data_expression', label: 'Script tạo file Excel (trả về JSON cấu hình)', type: 'Code',
			gridEdit: { xs: 12 }, helpText: huongDanExcel || HD_SCRIPT_EXCEL, hideInForm: la('excel_config'), required: true,
		},
		{
			// Với mẫu HTML/file: cùng trường đó chỉ là bước tiền xử lý dữ liệu, không bắt buộc
			field: 'handle_data_expression', label: nhan('handle_data_expression', 'Xử lý dữ liệu trước khi in hoặc xuất excel'),
			type: 'Code', gridEdit: { xs: 12 }, helpText: HD_XU_LY_DU_LIEU,
			hideInForm: (ctx, formData) => (formData || {})._loai_mau === 'excel_config' || chiNangCao(ctx, formData),
		},
		{ field: '_nang_cao', label: 'Hiện tuỳ chọn nâng cao (liên in, tham số)', type: 'Boolean', gridEdit: { xs: 12 } },
		{
			field: 'cac_lien', label: nhan('cac_lien', 'Liên'), type: 'ObjectArray', gridEdit: { xs: 12 },
			hideInForm: chiNangCao,
			helpText: 'Mỗi liên in ra một trang; trong mẫu HTML gọi tên liên bằng ${data.lien.ten_lien}.',
			subFields: [
				{ field: 'ma_lien', label: 'Mã liên', type: 'String', required: true, gridEdit: { xs: 4 } },
				{ field: 'ten_lien', label: 'Tên liên', type: 'String', required: true, isTitle: true, gridEdit: { xs: 8 } },
			],
		},
		{
			field: 'parameters', label: nhan('parameters', 'Tham số'), type: 'ObjectArray', gridEdit: { xs: 12 },
			hideInForm: chiNangCao, helpText: HD_THAM_SO,
			subFields: [
				{ field: 'name', label: 'Tên biến', type: 'String', required: true, isTitle: true, gridEdit: { xs: 12, sm: 4 } },
				{
					field: 'type', label: 'Kiểu', type: 'Radio', gridEdit: { xs: 12, sm: 8 },
					options: [
						{ label: 'Hằng số', value: '' }, { label: 'Số → chữ (W)', value: 'W' },
						{ label: 'Số → chữ Anh (EN)', value: 'EN' }, { label: 'Truy vấn (Q)', value: 'Q' },
						{ label: 'Biểu thức (E)', value: 'E' },
					],
				},
				{ field: 'value', label: 'Giá trị / tên field / biểu thức', type: 'String', gridEdit: { xs: 12 } },
			],
		},
	];

	// Field lạ do công ty tự thêm vào listinfo `rpt` -> vẫn cho sửa, xếp vào phần nâng cao.
	// KHÔNG kéo lại 2 field đã bỏ: `send_to` (gửi báo cáo qua email) và `bluetooth_template`
	// (in máy bluetooth) — không còn dùng nữa; chúng vẫn còn trong listinfo/model nên phải chặn ở đây,
	// nếu không vòng lặp dưới sẽ tự thêm lại vào form.
	const daCo = { send_to: true, bluetooth_template: true };
	fields.forEach((f) => { daCo[f.field] = true; });
	nguon.forEach((f) => {
		if (!f || !f.name || daCo[f.name] || f.type === 'Action') return;
		fields.push({
			field: f.name, label: f.header || f.name,
			type: KIEU_LISTINFO_SANG_FORM[f.type] || 'String',
			required: false, gridEdit: { xs: 12, sm: 6 }, hideInForm: chiNangCao,
		});
	});

	// Giữ đúng "bắt buộc" mà listinfo khai cho 2 field lõi
	fields.forEach((f) => {
		if (f.field === 'ma_cn' || f.field === 'ten_mau_in') f.required = batBuoc(f.field) !== false;
	});

	return fields;
}

/** Trường chỉ phục vụ giao diện, không được gửi lên server. */
export const boFieldGiaoDien = (formData = {}) => {
	const clean = { ...formData };
	Object.keys(clean).forEach((k) => { if (k.charAt(0) === '_' && k !== '_id') delete clean[k]; });
	return clean;
};
